import { Navigate } from 'react-router-dom';
import { cn, Spinner } from '@c2fo/liquidity';
import { useOfferBuilderContext } from '@/enterprise/components/OfferBuilder/OfferBuilderContext';
import HydrateAcquisitionRates from '@/enterprise/features/acquisition/HydrateAcquisitionRates';
import HydrateDraftRates from '@/enterprise/features/draftOffer/reviewLayout/HydrateDraftRates';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import Skeleton from '../../Skeleton';
import OfferReview from './OfferReview';
import { OfferReviewLoadingProvider, useOfferReviewLoadingContext } from './OfferReviewLoadingContext';

export const OfferReviewLayoutSkeleton = () => (
  <div>
    <div className="rounded-md bg-white p-6">
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex lg:w-7/12">
          <div className="w-full space-y-4 rounded-xl bg-deepBlue-50 p-4">
            <Skeleton className="h-20 w-1/3" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-20 w-2/3" />
          </div>
        </div>
        <div className="flex flex-1 flex-col">
          <div className="space-y-4">
            <Skeleton className="h-16 w-1/2" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-16 w-1/2" />
          </div>
        </div>
      </div>
      <div className="mb-6 mt-8 h-[0.5px] bg-stroke/30" />
      <div className="mt-4 flex flex-col gap-6 lg:mt-0 lg:flex-row">
        <div className="space-y-4 lg:w-7/12">
          <div className="space-y-2">
            <Skeleton className="h-16 w-2/3" />
          </div>
          <div className="space-y-3 rounded-xl bg-deepBlue-50 p-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-24 w-full" />
            ))}
          </div>
        </div>
        <div className="flex-1">
          <div className="space-y-4">
            <Skeleton className="h-16 w-2/3" />
            <div className="space-y-3 rounded-xl bg-deepBlue-50 p-4">
              {Array.from({ length: 7 }).map((_, index) => (
                <Skeleton key={index} className="h-16 w-full" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const OfferReviewLayoutComponent = () => {
  const { isPending } = useOfferReviewLoadingContext();
  const {
    state: { builderVariant, draftOfferProperties },
  } = useOfferBuilderContext();

  const shouldHydrateDraftRates =
    builderVariant === 'draft' &&
    draftOfferProperties?.draftType === 'ACTIONABLE_OFFER' &&
    (draftOfferProperties?.offerType === 'EXPRESS_ACCEPT' || draftOfferProperties?.offerType === 'PREFERRED');

  const isAcquisition = builderVariant === 'acquisition';

  return (
    <div className="relative">
      <div className={cn({ 'blur-sm': isPending })}>
        {shouldHydrateDraftRates && <HydrateDraftRates />}
        {isAcquisition && <HydrateAcquisitionRates />}
        <div>
          <OfferReview />
        </div>
      </div>
      {isPending && (
        <div className="absolute -inset-1.5 flex items-center justify-center rounded-2xl bg-canvas/50">
          <Spinner className="h-12 w-12 fill-primary-500" />
        </div>
      )}
    </div>
  );
};

const OfferReviewLayout = () => {
  const {
    state: { selected, submissionComplete },
  } = useOfferBuilderContext();

  if (selected.length === 0 || submissionComplete) {
    return <Navigate to={asEnterpriseLink('build-offer')} replace />;
  }

  return (
    <QueryBoundaries LoadingComponent={() => <OfferReviewLayoutSkeleton />}>
      <OfferReviewLoadingProvider>
        <OfferReviewLayoutComponent />
      </OfferReviewLoadingProvider>
    </QueryBoundaries>
  );
};

export default OfferReviewLayout;
